import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import { getToken } from "../../utils/tokenUtils";
// import PayrollLayout from "../../layouts/PayrollLayout";
import Notification from "../../components/UI/Notification";
import { useUsers } from "../../contexts/UsersContext";
import ReactPaginate from "react-paginate";
import DropdownMenu from "../../components/UI/DropdownMenu";
import { MoreVertical } from "lucide-react";
import { X } from "lucide-react";

export default function SalaryStructure() {
  const API_URL = "https://hrmspayroll.prepstripe.com/api";
  const token = getToken();
  const [notification, setNotification] = useState(null);
  const { allUsersWithEmployeeIds, fetchAllUsersWithEmployeeIds, fetchUsers } =
    useUsers();
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    perPage: 20,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [structures, setStructures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [openMenu, setOpenMenu] = useState(null);

  // filters & pagination
  const [filterActive, setFilterActive] = useState("");
  const [filterEffectiveDate, setFilterEffectiveDate] = useState("");
  const [search, setSearch] = useState("");
  const [perPage, setPerPage] = useState(20);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  // modal + form
  const [showModal, setShowModal] = useState(false);
  const [modalTitle, setModalTitle] = useState("Add Salary Structure");
  const [editingEmployeeId, setEditingEmployeeId] = useState(null);
  const [form, setForm] = useState({
    employee_id: "",
    basic_salary: "",
    gross_salary: "",
    ctc: "",
    effective_from: "",
    earnings_components: [],
    deduction_components: [],
    variable_components: [],
  });

  // history modal
  const [showHistory, setShowHistory] = useState(false);
  const [historyEmployee, setHistoryEmployee] = useState(null);
  const [historyItems, setHistoryItems] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // bulk update modal
  const [showBulk, setShowBulk] = useState(false);
  const [bulkEffectiveFrom, setBulkEffectiveFrom] = useState("");
  const [bulkText, setBulkText] = useState(""); // JSON lines: [{employee_id, percentage_increase, fixed_increase}]
  // Bulk updates rows for percentage/fixed increases
  const [bulkUpdates, setBulkUpdates] = useState([
    {
      employee_id: "",
      percentage_increase: "",
      fixed_increase: "",
      update_type: "percentage",
    },
  ]);

  // export/import
  const [exportDept, setExportDept] = useState("");
  const [exportDesig, setExportDesig] = useState("");
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  // Add these state variables to your existing state declarations
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templateData, setTemplateData] = useState(null);
  const [templateLoading, setTemplateLoading] = useState(false);

  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationData, setVerificationData] = useState(null);

  console.log("verificationData:", verificationData);

  const api = useMemo(() => {
    return axios.create({
      baseURL: API_URL,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
  }, [token]);

  useEffect(() => {
    fetchAllUsersWithEmployeeIds();
  }, []);

  const addBulkUpdateRow = () => {
    setBulkUpdates((prev) => [
      ...prev,
      {
        employee_id: "",
        percentage_increase: "",
        fixed_increase: "",
        update_type: "percentage",
      },
    ]);
  };

  const updateBulkUpdateRow = (index, field, value) => {
    setBulkUpdates((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };

      // Clear the opposite field when update_type changes
      if (field === "update_type") {
        if (value === "percentage") {
          updated[index].fixed_increase = "";
        } else {
          updated[index].percentage_increase = "";
        }
      }

      return updated;
    });
  };

  const removeBulkUpdateRow = (index) => {
    setBulkUpdates((prev) => prev.filter((_, i) => i !== index));
  };

  const resetBulkForm = () => {
    setBulkUpdates([
      {
        employee_id: "",
        percentage_increase: "",
        fixed_increase: "",
        update_type: "percentage",
      },
    ]);
    setBulkEffectiveFrom("");
  };

  const addComponentRow = (key) => {
    setForm((prev) => ({
      ...prev,
      [key]: [
        ...prev[key],
        { name: "", amount: "", config_id: "", is_taxable: false },
      ],
    }));
  };

  const updateComponentRow = (key, idx, patch) => {
    setForm((prev) => {
      const rows = [...prev[key]];
      rows[idx] = { ...rows[idx], ...patch };
      return { ...prev, [key]: rows };
    });
  };

  const removeComponentRow = (key, idx) => {
    setForm((prev) => {
      const rows = [...prev[key]];
      rows.splice(idx, 1);
      return { ...prev, [key]: rows };
    });
  };

  const resetForm = () => {
    setForm({
      employee_id: "",
      basic_salary: "",
      gross_salary: "",
      ctc: "",
      effective_from: "",
      earnings_components: [],
      deduction_components: [],
      variable_components: [],
    });
    setEditingEmployeeId(null);
    setModalTitle("Add Salary Structure");
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const loadList = useCallback(
    async (page = 1) => {
      setLoading(true);
      setError("");
      try {
        const params = { per_page: perPage, page };
        if (filterActive !== "") params.active = filterActive;
        if (filterEffectiveDate) params.effective_date = filterEffectiveDate;
        if (search) params.search = search;
        const res = await api.get(
          `/payroll/salary-structures?${(page = page)}`,
          { params }
        );
        const data = res?.data.data || {};
        // set pagination
        setPagination({
          currentPage: res.data?.data.current_page || 1,
          totalPages: res.data?.data.last_page || 1,
          totalItems: res.data?.data.total || 0,
          perPage: res.data?.data.per_page || 10,
        });
        const list = Array.isArray(data) ? data : data.data || [];
        setStructures(list);
        // derive pagination meta if present
        const meta = data.meta || {};
        setTotal(meta.total || list.length);
      } catch (e) {
        console.error(e);
        setNotification({
          message: e.message,
          type: "error",
        });
        setError("Failed to load salary structures");
      } finally {
        setLoading(false);
      }
    },
    [api, filterActive, filterEffectiveDate, perPage, page, search]
  );

  useEffect(() => {
    loadList();
  }, [loadList]);

  const onEdit = async (employeeId) => {
    setError("");
    try {
      const res = await api.get(`/payroll/salary-structures/${employeeId}`);
      const s = res?.data || {};
      setEditingEmployeeId(employeeId);
      setForm({
        employee_id: s.data.employee_id ?? employeeId ?? "",
        basic_salary: s.data.basic_salary ?? "",
        gross_salary: s.data.gross_salary ?? "",
        ctc: s.data.ctc ?? "",
        effective_from: s.data.effective_from
          ? String(s.data.effective_from).slice(0, 10)
          : "",
        earnings_components: s.data.earnings_components || [],
        deduction_components: s.data.deduction_components || [],
        variable_components: s.data.variable_components || [],
      });
      setModalTitle("Edit Salary Structure");
      setShowModal(true);
    } catch (e) {
      console.error(e);
      setNotification({
        message: e.message,
        type: "error",
      });
      setError("Failed to load salary structure");
    }
  };

  // Actions menu component for table rows
  const ActionsColumn = React.memo(({ row }) => {
    const buttonRef = useRef(null);
    return (
      <div className="flex justify-center items-center relative">
        <button
          ref={buttonRef}
          onClick={() =>
            setOpenMenu(
              openMenu === (row.id || row.employee_id)
                ? null
                : row.id || row.employee_id
            )
          }
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
        >
          <MoreVertical className="w-4 h-4 text-gray-600 dark:text-gray-400" />
        </button>

        {openMenu === (row.id || row.employee_id) && (
          <DropdownMenu buttonRef={buttonRef} onClose={() => setOpenMenu(null)}>
            <button
              onClick={() => {
                openHistory(row.employee_id);
                setOpenMenu(null);
              }}
              className="w-full text-left px-3 py-2 text-gray-700 text-sm hover:bg-blue-50 transition-colors"
            >
              History
            </button>
            <button
              onClick={() => {
                onEdit(row.employee_id);
                setOpenMenu(null);
              }}
              className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 transition-colors"
            >
              Edit
            </button>
          </DropdownMenu>
        )}
      </div>
    );
  });

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const body = {
        ...form,
        employee_id: String(form.employee_id).trim(),
        basic_salary:
          form.basic_salary === "" ? undefined : Number(form.basic_salary),
        gross_salary:
          form.gross_salary === "" ? undefined : Number(form.gross_salary),
        ctc: form.ctc === "" ? undefined : Number(form.ctc),
        earnings_components: (form.earnings_components || []).map((c) => ({
          ...c,
          amount: c.amount === "" ? undefined : Number(c.amount),
          config_id: c.config_id === "" ? undefined : Number(c.config_id),
          is_taxable: !!c.is_taxable,
        })),
        deduction_components: (form.deduction_components || []).map((c) => ({
          ...c,
          amount: c.amount === "" ? undefined : Number(c.amount),
          config_id: c.config_id === "" ? undefined : Number(c.config_id),
          is_taxable: !!c.is_taxable,
        })),
        variable_components: (form.variable_components || []).map((c) => ({
          ...c,
          amount: c.amount === "" ? undefined : Number(c.amount),
          config_id: c.config_id === "" ? undefined : Number(c.config_id),
          is_taxable: !!c.is_taxable,
        })),
      };
      const res = await api.post("/payroll/salary-structures", body);
      setStructures((prev) => [res.data.data, ...prev]);
      setNotification({
        message: res.data?.message || "Salary structure added successfully",
        type: "success",
      });
      setShowModal(false);
      resetForm();
      // await loadList();
    } catch (e) {
      console.error(e);

      // error handling
      let message = "Failed to save salary structure";

      if (e.response && e.response.data && e.response.data.error) {
        const errorData = e.response.data.error;

        if (typeof errorData === "string") {
          // plain string error
          message = errorData;
        } else if (typeof errorData === "object") {
          // object with field errors
          message = Object.entries(errorData)
            .map(([field, errors]) => {
              if (Array.isArray(errors)) {
                return `${field}: ${errors.join(", ")}`;
              }
              return `${field}: ${errors}`;
            })
            .join(" | ");
        }
      } else if (e.message) {
        message = e.message;
      }

      setNotification({
        message: e.message,
        type: "error",
      });
      setError(message);
    }
  };

  const onGenerateTemplate = async () => {
    setError("");
    try {
      const res = await api.post(
        "/payroll/salary-structures/generate-template",
        {
          basic_salary:
            form.basic_salary === "" ? undefined : Number(form.basic_salary),
        }
      );
      const tpl = res?.data || {};
      setNotification({
        message: res.data?.message || "Template generated successfully",
        type: "success",
      });
      setForm((prev) => ({
        ...prev,
        earnings_components:
          tpl.earnings_components || prev.earnings_components,
        deduction_components:
          tpl.deduction_components || prev.deduction_components,
        variable_components:
          tpl.variable_components || prev.variable_components,
        gross_salary: tpl.gross_salary ?? prev.gross_salary,
        ctc: tpl.ctc ?? prev.ctc,
      }));
    } catch (e) {
      console.error(e);
      setNotification({
        message: e.message,
        type: "error",
      });
      setError("Failed to generate template");
    }
  };

  const openHistory = async (employeeId) => {
    setHistoryEmployee(employeeId);
    setShowHistory(true);
    setHistoryLoading(true);
    setError("");
    try {
      const res = await api.get(
        `/payroll/salary-structures/${employeeId}/history`,
        { params: { per_page: 20 } }
      );
      const data = res?.data || {};
      setHistoryItems(Array.isArray(data) ? data : data.data.data || []);
    } catch (e) {
      console.error(e);
      setNotification({
        message: e.message,
        type: "error",
      });
      setError("Failed to load history");
    } finally {
      setHistoryLoading(false);
    }
  };

  const onBulkUpdate = async () => {
    setError("");
    try {
      // Prefer the structured rows UI; fallback to JSON textarea only if rows are empty
      let updates = (bulkUpdates || [])
        .filter((u) => u && u.employee_id)
        .map((u) => {
          const payload = { employee_id: u.employee_id };
          if (u.update_type === "percentage") {
            const val =
              u.percentage_increase === ""
                ? undefined
                : Number(u.percentage_increase);
            if (typeof val === "number" && !Number.isNaN(val))
              payload.percentage_increase = val;
          } else {
            const val =
              u.fixed_increase === "" ? undefined : Number(u.fixed_increase);
            if (typeof val === "number" && !Number.isNaN(val))
              payload.fixed_increase = val;
          }
          return payload;
        })
        .filter(
          (u) =>
            u.percentage_increase !== undefined ||
            u.fixed_increase !== undefined
        );

      if ((!updates || updates.length === 0) && bulkText) {
        try {
          updates = JSON.parse(bulkText || "[]");
        } catch {
          alert("Invalid JSON. Please provide an array of updates.");
          return;
        }
      }
      const body = {
        updates,
        effective_from: bulkEffectiveFrom,
      };
      await api.post("/payroll/salary-structures/bulk-update", body);
      setShowBulk(false);
      setBulkText("");
      setBulkEffectiveFrom("");
      setBulkUpdates([
        {
          employee_id: "",
          percentage_increase: "",
          fixed_increase: "",
          update_type: "percentage",
        },
      ]);
      await loadList();
    } catch (e) {
      console.error(e);
      setError("Failed to perform bulk update");
    }
  };

  // Modified onDownloadTemplate function
  const onDownloadTemplate = async () => {
    setTemplateLoading(true);
    setShowTemplateModal(true);
    setTemplateData(null);

    try {
      // Fetch template info without triggering download
      const res = await api.get("/salary-structures/template/download", {
        headers: {
          "Content-Type": "multipart/form-data",
          Accept: "application/json",
        },
      });

      const { file, message, ...otherData } = res.data;

      // Set the template data to display in modal
      setTemplateData({
        message: message || "Template information retrieved successfully",
        file: file,
        downloadUrl: file?.download_url,
        filename: file?.filename || "salary_structures_template.csv",
        ...otherData, // Include any other data returned by the API
      });
    } catch (e) {
      console.error("Template error:", e);
      setTemplateData({
        error: true,
        message: e.message || "Failed to get template information",
      });
    } finally {
      setTemplateLoading(false);
    }
  };

  // Function to actually download the file from the modal
  const downloadTemplateFile = () => {
    if (templateData?.downloadUrl) {
      const link = document.createElement("a");
      link.href = templateData.downloadUrl;
      link.setAttribute("download", templateData.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    }
  };

  // Template Response Modal Component (add this to your JSX)
  const TemplateResponseModal = () => {
    if (!showTemplateModal) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1001] p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
          <div>
            <div className="relative">
              <div className="absolute inset-0 dark:from-gray-800 dark:to-gray-850"></div>
              <div className="relative flex items-center justify-between px-4 py-3">
                <div className="flex items-center space-x-3">
                  {/* <div className="w-1 h-6 bg-gradient-to-b from-blue-500 to-purple-600 rounded-full"></div> */}
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white tracking-tight">
                    Template Information
                  </h2>
                </div>

                {/* Enhanced close button */}
                <button
                  type="button"
                  onClick={() => setShowTemplateModal(false)}
                  className="group relative p-2 rounded-xl transition-all duration-200 hover:scale-105 active:scale-95"
                  aria-label="Close modal"
                >
                  <X
                    size={20}
                    className="text-gray-600 transition-colors duration-200"
                  />
                  <div className="absolute inset-0 rounded-xl transition-all duration-200"></div>
                </button>
              </div>
            </div>

            <div className="space-y-4">
              {templateLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3"></div>
                  Loading template information...
                </div>
              ) : templateData ? (
                <>
                  {/* Message */}
                  <div
                    className={`p-4 rounded-lg my-4 mx-2 ${
                      templateData.error
                        ? "bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800"
                        : "bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800"
                    }`}
                  >
                    <p
                      className={`font-medium ${
                        templateData.error
                          ? "text-red-700 dark:text-red-300"
                          : "text-green-700 dark:text-green-300"
                      }`}
                    >
                      {templateData.message}
                    </p>
                  </div>

                  {/* File Information */}
                  {templateData.file && !templateData.error && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                      <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                        File Details
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">
                            Filename:
                          </span>
                          <span className="font-medium text-gray-900 dark:text-white">
                            {templateData.filename}
                          </span>
                        </div>
                        {templateData.file.size && (
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">
                              File Size:
                            </span>
                            <span className="font-medium text-gray-900 dark:text-white">
                              {templateData.file.size}
                            </span>
                          </div>
                        )}
                        {templateData.downloadUrl && (
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">
                              Download URL:
                            </span>
                            <a
                              href={templateData.downloadUrl}
                              download={templateData.filename}
                              className="font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 text-xs break-all underline transition-colors"
                              onClick={(e) => {
                                e.preventDefault();
                                downloadTemplateFile();
                              }}
                            >
                              {templateData.downloadUrl.substring(0, 50)}...
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Any other data from the API response */}
                  {/* Any other data from the API response */}
                  {templateData &&
                    !templateData.error &&
                    Object.keys(templateData).some(
                      (key) =>
                        ![
                          "message",
                          "file",
                          "downloadUrl",
                          "filename",
                          "error",
                        ].includes(key)
                    ) && (
                      <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                        <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                          Additional Information
                        </h4>
                        <div className="space-y-3">
                          {Object.entries(templateData)
                            .filter(
                              ([key]) =>
                                ![
                                  "message",
                                  "file",
                                  "downloadUrl",
                                  "filename",
                                  "error",
                                ].includes(key)
                            )
                            .map(([key, value]) => (
                              <div key={key} className="flex flex-col gap-1">
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300 capitalize">
                                  {key.replace(/_/g, " ")}:
                                </span>
                                <div className="text-sm text-gray-600 dark:text-gray-400 ml-2">
                                  {typeof value === "object" &&
                                  value !== null ? (
                                    Array.isArray(value) ? (
                                      value.length > 0 ? (
                                        <ul className="list-disc list-inside space-y-1">
                                          {value.map((item, index) => (
                                            <li key={index}>
                                              {typeof item === "object" ? (
                                                <div className="ml-4 space-y-1">
                                                  {Object.entries(item).map(
                                                    ([itemKey, itemValue]) => (
                                                      <div
                                                        key={itemKey}
                                                        className="flex gap-2"
                                                      >
                                                        <span className="font-medium">
                                                          {itemKey.replace(
                                                            /_/g,
                                                            " "
                                                          )}
                                                          :
                                                        </span>
                                                        <span>
                                                          {String(itemValue)}
                                                        </span>
                                                      </div>
                                                    )
                                                  )}
                                                </div>
                                              ) : (
                                                String(item)
                                              )}
                                            </li>
                                          ))}
                                        </ul>
                                      ) : (
                                        <span className="text-gray-500 italic">
                                          Empty array
                                        </span>
                                      )
                                    ) : (
                                      <div className="space-y-1">
                                        {Object.entries(value).map(
                                          ([nestedKey, nestedValue]) => (
                                            <div
                                              key={nestedKey}
                                              className="flex gap-2"
                                            >
                                              <span className="font-medium">
                                                {nestedKey.replace(/_/g, " ")}:
                                              </span>
                                              <span>{String(nestedValue)}</span>
                                            </div>
                                          )
                                        )}
                                      </div>
                                    )
                                  ) : (
                                    <span>{String(value)}</span>
                                  )}
                                </div>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                </>
              ) : (
                <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                  No data available
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end space-x-3 px-2 py-2">
              <button
                onClick={() => setShowTemplateModal(false)}
                className="px-12 py-2 text-black hover:bg-gray-200 dark:hover:bg-gray-900 dark:bg-black dark:text-white dark:focus:outline-none border border-gray-300 rounded-md"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Updated CSV Import function
  const onImportCsv = async () => {
    if (!importFile) {
      setNotification({
        message: "Please select a CSV file to import",
        type: "error",
      });
      return;
    }

    setImportLoading(true);
    setError("");
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append("file", importFile);

      const res = await api.post(
        "/payroll/salary-structures/import",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Accept: "application/json",
          },
        }
      );

      const result = res.data;
      setImportResult(result);

      // Show success notification with summary
      setNotification({
        message: result.message || "CSV import completed successfully",
        type: result.summary.has_errors ? "warning" : "success",
      });

      // Clear the selected file
      setImportFile(null);
      setShowImportModal(false);

      // Reload the list to show updated data
      await loadList();
    } catch (e) {
      console.error("Import error:", e);

      let errorMessage = "Failed to import CSV";

      if (e.response && e.response.data) {
        errorMessage =
          e.response.data.message || e.response.data.error || errorMessage;
      } else if (e.message) {
        errorMessage = e.message;
      }

      setNotification({
        message: errorMessage,
        type: "error",
      });
      setError(errorMessage);
    } finally {
      setImportLoading(false);
    }
  };

  // Modified onExportVerification function
  const onExportVerification = async (options = {}) => {
    setVerifyLoading(true);
    setShowVerificationModal(true);
    setVerificationData(null);
    setError("");

    try {
      const params = {};

      // Add optional filters
      if (options.history) params.history = "1";
      if (options.asJson) params.as = "json";
      if (exportDept) params.department = exportDept;
      if (exportDesig) params.designation = exportDesig;

      const res = await api.get("/salary-structures/export/verify", {
        params,
        responseType: options.asJson ? "json" : "json", // Changed to always get JSON first
      });

      // Set the verification data to display in modal
      setVerificationData({
        message: res.data.message || "Verification data retrieved successfully",
        data: res.data,
        downloadAvailable: !options.asJson, // Show download option unless specifically requested as JSON only
        filters: {
          department: exportDept,
          designation: exportDesig,
          ...options,
        },
      });

      setNotification({
        message: res.data.message || "Verification data retrieved successfully",
        type: "success",
      });
    } catch (e) {
      console.error("Export verification error:", e);
      setNotification({
        message:
          e.response?.data?.message ||
          e.message ||
          "Failed to export verification data",
        type: "error",
      });
      setVerificationData({
        error: true,
        message:
          e.response?.data?.message ||
          e.message ||
          "Failed to export verification data",
        details: e.response?.data || null,
      });
    } finally {
      setVerifyLoading(false);
    }
  };

  // Function to download the actual file from the modal
  const downloadVerificationFile = async () => {
    try {
      const params = {};
      if (exportDept) params.department = exportDept;
      if (exportDesig) params.designation = exportDesig;

      const res = await api.get("/salary-structures/export/verify", {
        params,
        responseType: "blob",
      });

      const contentDisposition = res.headers["content-disposition"];
      let filename = "salary_structures_verification.csv";

      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match) filename = match[1];
      }

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setNotification({
        message: "Verification file downloaded successfully",
        type: "success",
      });
    } catch (e) {
      console.error("Download error:", e);
      setNotification({
        message:
          e.response?.data?.message || "Failed to download verification file",
        type: "error",
      });
    }
  };

  // Verification Response Modal Component
  const VerificationResponseModal = () => {
    if (!showVerificationModal || !verificationData?.data) return null;

    const { file, next_steps } = verificationData.data;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1001] p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Export Verification Results
              </h3>
              <button
                onClick={() => setShowVerificationModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              {file && (
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                    File Details
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">
                        Filename:
                      </span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {file.filename}
                      </span>
                    </div>
                    {file.download_url && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">
                          Download:
                        </span>
                        <a
                          href={file.download_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 underline transition-colors"
                        >
                          Download File
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {next_steps && next_steps.length > 0 && (
                <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                    Next Steps
                  </h4>
                  <ol className="list-decimal list-inside space-y-1 text-sm text-gray-600 dark:text-gray-400">
                    {next_steps.map((step, index) => (
                      <li key={index}>{step}</li>
                    ))}
                  </ol>
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowVerificationModal(false)}
                className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const onExportCsv = async () => {
    try {
      const res = await api.get("/salary-structures/export/verify", {
        params: {
          department: exportDept || undefined,
          designation: exportDesig || undefined,
        },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "salary_structures_export.csv");
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (e) {
      console.error(e);
      setNotification({
        message: e.message,
        type: "error",
      });
      setError("Failed to export CSV");
    }
  };

  // Component for displaying import results
  const ImportResultsModal = () => {
    if (!importResult) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1001] p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
          <div className="">
            <div className="relative">
              <div className="absolute inset-0 dark:from-gray-800 dark:to-gray-850"></div>
              <div className="relative flex items-center justify-between py-3 px-4 border-b border-gray-300 shadow dark:border-gray-700/50">
                <div className="flex items-center space-x-3">
                  {/* <div className="w-1 h-6 bg-gradient-to-b from-blue-500 to-purple-600 rounded-full"></div> */}
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white tracking-tight">
                    Image Results
                  </h2>
                </div>

                {/* Enhanced close button */}
                <button
                  type="button"
                  onClick={() => setImportResult(null)}
                  className="group relative p-2 rounded-xl  hover:bg-red-50 dark:bg-gray-800/80 dark:hover:bg-red-900/20 transition-all duration-200 hover:scale-105 active:scale-95"
                  aria-label="Close modal"
                >
                  <X
                    size={20}
                    className="text-gray-600 group-hover:text-red-500 dark:text-gray-400 dark:group-hover:text-red-400 transition-colors duration-200"
                  />
                  <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-red-500/0 to-red-600/0 group-hover:from-red-500/10 group-hover:to-red-600/10 transition-all duration-200"></div>
                </button>
              </div>
            </div>

            <div className="space-y-4">
              {/* Summary */}
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-md">
                <h4 className="font-medium text-gray-900 dark:text-white mb-4">
                  Summary
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">
                      Processed:
                    </span>
                    <span className="ml-2 font-medium">
                      {importResult.summary.processed}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">
                      Success:
                    </span>
                    <span className="ml-2 font-medium text-green-600">
                      {importResult.summary.success}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">
                      Errors:
                    </span>
                    <span className="ml-2 font-medium text-red-600">
                      {importResult.summary.errors_count}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">
                      Warnings:
                    </span>
                    <span className="ml-2 font-medium text-yellow-600">
                      {importResult.summary.warnings_count}
                    </span>
                  </div>
                </div>
              </div>

              {/* Archive Info */}
              {importResult.archive && (
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-md mx-4">
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                    Archive
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    File: {importResult.archive.filename}
                  </p>
                  {importResult.archive.url && (
                    <a
                      href={importResult.archive.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 text-sm underline"
                    >
                      Download Archive
                    </a>
                  )}
                </div>
              )}

              {/* Next Steps */}
              {importResult.next_steps &&
                importResult.next_steps.length > 0 && (
                  <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-md mx-4">
                    <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                      Next Steps
                    </h4>
                    <ol className="list-decimal list-inside space-y-1 text-sm text-gray-600 dark:text-gray-400">
                      {importResult.next_steps.map((step, index) => (
                        <li key={index}>{step}</li>
                      ))}
                    </ol>
                  </div>
                )}

              {/* Errors */}
              {importResult.data?.errors &&
                importResult.data.errors.length > 0 && (
                  <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-md">
                    <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                      Errors
                    </h4>
                    <ul className="space-y-1 text-sm text-red-600 dark:text-red-400">
                      {importResult.data.errors.map((error, index) => (
                        <li key={index}>• {error}</li>
                      ))}
                    </ul>
                  </div>
                )}

              {/* Warnings */}
              {importResult.data?.warnings &&
                importResult.data.warnings.length > 0 && (
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-md">
                    <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                      Warnings
                    </h4>
                    <ul className="space-y-1 text-sm text-yellow-600 dark:text-yellow-400">
                      {importResult.data.warnings.map((warning, index) => (
                        <li key={index}>• {warning}</li>
                      ))}
                    </ul>
                  </div>
                )}
            </div>

            <div className="mt-6 flex justify-end gap-2 p-2">
              <button
                onClick={() => setImportResult(null)}
                className="px-12 py-2 text-black hover:bg-gray-200 dark:hover:bg-gray-900 dark:bg-black dark:text-white dark:focus:outline-none border border-gray-300 rounded-md"
              >
                Close
              </button>
              <button
                onClick={() => onExportVerification()}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium transition-colors"
                disabled={verifyLoading}
              >
                {verifyLoading ? "Exporting..." : "Verify & Export"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setCurrentPage(newPage);
      loadList(newPage);
    }
  };

  return (
    <div className="flex flex-col gap-y-4 font-poppins bg-white mt-2">
      {/* notification */}
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      {/* Header Section */}
      <div className="bg-white dark:bg-gray-900 p-2 mx-2 font-poppins">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
          {/* Title + Description */}
          <div className="flex-1">
            <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 dark:text-white">
              Salary Structures
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mt-1 text-sm sm:text-base">
              Manage employee salary structures and components
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row flex-wrap gap-1 w-full lg:w-auto">
            {/* Primary Actions */}
            <div className="flex flex-col sm:flex-row gap-1 w-full sm:w-auto">
              <button
                onClick={openCreateModal}
                className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg font-medium transition-colors shadow-sm w-full sm:w-auto"
              >
                + Add Structure
              </button>
              <button
                onClick={() => setShowBulk(true)}
                className="border-[1px] border-blue-500 hover:bg-blue-50 duration-200 text-blue-500 px-3 py-2 rounded-lg font-medium transition-colors shadow-sm w-full sm:w-auto"
              >
                Bulk Update
              </button>
            </div>

            {/* Template & Import/Export Section */}
            <div className="flex flex-col sm:flex-row flex-wrap gap-1 w-full sm:w-auto">
              <button
                onClick={onDownloadTemplate}
                className="border-[1px] border-blue-500 hover:bg-blue-50 duration-200 text-blue-600 px-3 py-2 rounded-lg font-medium transition-colors shadow-sm w-full sm:w-auto"
              >
                Download Template
              </button>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
                <button
                  onClick={() => setShowImportModal(true)}
                  className="border-[1px] border-blue-500 hover:bg-blue-50 duration-200 text-blue-500 px-3 py-2 rounded-lg font-medium cursor-pointer transition-colors shadow-sm w-full sm:w-auto text-center"
                >
                  Import
                </button>
                {importFile && (
                  <button
                    onClick={onImportCsv}
                    className="outline outline-blue-600 text-blue-500 px-3 py-2 rounded-lg font-medium transition-colors shadow-sm w-full sm:w-auto"
                  >
                    Upload CSV
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Import Results Modal */}
      <ImportResultsModal />

      <TemplateResponseModal />

      <VerificationResponseModal />

      {/* Filters Section */}
      <div className="bg-white dark:bg-gray-900 p-2 mx-2">
        <h3 className="text-lg font-semibold dark:text-white mb-4">
          Filter Options
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Status
            </label>
            <select
              className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:border-gray-400 text-sm"
              value={filterActive}
              onChange={(e) => setFilterActive(e.target.value)}
            >
              <option value="">All Status</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Effective Date
            </label>
            <input
              type="date"
              className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:border-gray-400 text-sm"
              value={filterEffectiveDate}
              onChange={(e) => setFilterEffectiveDate(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-2 md:col-span-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Search Employee
            </label>
            <input
              className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:border-gray-400 placeholder:text-sm"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or employee ID"
            />
          </div>
          <div className="flex items-end gap-3 mb-1">
            {(filterActive === "true" ||
              filterActive === "false" ||
              filterEffectiveDate ||
              search) && (
              <button
                onClick={() => {
                  setFilterActive("");
                  setFilterEffectiveDate("");
                  setSearch("");
                  setPerPage(20);
                  setPage(1);
                  loadList();
                }}
                className="px-4 py-2 outline outline-gray-500 text-gray-500 rounded-lg transition-colors font-medium"
              >
                Reset
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className="overflow-x-auto bg-white dark:bg-gray-900 shadow-md rounded-lg font-poppins mx-4">
        <div className="overflow-x-auto bg-white dark:bg-gray-900 shadow-md rounded-lg font-poppins">
          <table className="min-w-full table-auto md:table-fixed text-sm">
            <thead className="bg-gray-100 dark:bg-gray-800 dark:text-white">
              <tr>
                <th className="whitespace-nowrap text-left px-6 py-4 font-semibold text-black dark:text-gray-300">
                  EMP ID
                </th>
                <th className="whitespace-nowrap text-left px-6 py-4 font-semibold text-black dark:text-gray-300">
                  Basic Salary
                </th>
                <th className="whitespace-nowrap text-left px-6 py-4 font-semibold text-black dark:text-gray-300">
                  Gross Salary
                </th>
                <th className="whitespace-nowrap text-left px-6 py-4 font-semibold text-black dark:text-gray-300">
                  CTC
                </th>
                <th className="whitespace-nowrap text-left px-6 py-4 font-semibold text-black dark:text-gray-300">
                  Effective From
                </th>
                <th className="whitespace-nowrap text-center px-6 py-4 font-semibold text-black dark:text-gray-300">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td
                    className="px-6 py-8 text-center text-gray-500 dark:text-gray-400"
                    colSpan={6}
                  >
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-2"></div>
                      Loading...
                    </div>
                  </td>
                </tr>
              ) : structures.length === 0 ? (
                <tr>
                  <td
                    className="px-6 py-8 text-center text-gray-500 dark:text-gray-400"
                    colSpan={6}
                  >
                    No salary structures found
                  </td>
                </tr>
              ) : (
                structures.map((s) => (
                  <tr
                    key={s.id || s.employee_id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-800/50"
                  >
                    <td className="px-6 py-4 text-gray-900 dark:text-white font-medium">
                      {s.employee_name || s.employee_id}
                    </td>
                    <td className="px-6 py-4 text-gray-900 dark:text-gray-300">
                      ₹{s.basic_salary?.toLocaleString() || "N/A"}
                    </td>
                    <td className="px-6 py-4 text-gray-900 dark:text-gray-300">
                      ₹{s.gross_salary?.toLocaleString() || "N/A"}
                    </td>
                    <td className="px-6 py-4 text-gray-900 dark:text-gray-300">
                      ₹{s.ctc?.toLocaleString() || "N/A"}
                    </td>
                    <td className="px-6 py-4 text-gray-900 dark:text-gray-300">
                      {s.effective_from.slice(8, 10)}-
                      {s.effective_from.slice(5, 7)}-
                      {s.effective_from.slice(0, 4)}
                    </td>
                    <td className="px-6 py-4">
                      <ActionsColumn row={s} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* pagination compo */}
      <ReactPaginate
        breakLabel="..."
        nextLabel="Next >"
        onPageChange={(event) => handlePageChange(event.selected + 1)}
        forcePage={currentPage - 1}
        pageRangeDisplayed={5}
        disabledClassName="opacity-50 cursor-not-allowed"
        disabledLinkClassName="pointer-events-none"
        pageCount={pagination.totalPages}
        previousLabel="< Prev"
        previousClassName="dark:text-white border border-gray-300 rounded-md dark:border-gray-600"
        previousLinkClassName="block w-full h-full px-3 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
        nextClassName="dark:text-white border border-gray-300 rounded-md dark:border-gray-600"
        nextLinkClassName="block w-full h-full px-3 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
        renderOnZeroPageCount={null}
        containerClassName="flex items-center justify-center mb-4 font-poppins"
        pageClassName="mx-1 border rounded dark:text-white font-poppins"
        pageLinkClassName="block w-full h-full px-3 py-1 cursor-pointer"
        activeClassName="bg-blue-500 text-white dark:bg-black dark:text-white dark:border-gray-700 font-poppins"
        activeLinkClassName="bg-blue-500 text-white dark:bg-black dark:text-white hover:bg-blue-600 dark:hover:bg-gray-900"
      />

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 flex justify-center items-end md:items-center z-[1001] backdrop-blur-md transition-all duration-300">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowModal(false)}
          ></div>
          <div className="relative w-full max-w-5xl bg-white dark:bg-gray-900 rounded-md shadow-xl max-h-[90vh] overflow-hidden">
            <div className="relative">
              <div className="absolute inset-0 dark:from-gray-800 dark:to-gray-850"></div>
              <div className="relative flex items-center justify-between py-3 px-6 ">
                <div className="flex items-center space-x-3">
                  {/* <div className="w-1 h-6 bg-gradient-to-b from-blue-500 to-purple-600 rounded-full"></div> */}
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white tracking-tight">
                    {modalTitle}
                  </h2>
                </div>

                {/* Enhanced close button */}
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="group relative p-2 rounded-xl transition-all duration-200 hover:scale-105 active:scale-95"
                  aria-label="Close modal"
                >
                  <X
                    size={20}
                    className="text-gray-600 transition-colors duration-200"
                  />
                  <div className="absolute inset-0 rounded-xl transition-all duration-200"></div>
                </button>
              </div>
            </div>

            <div className="overflow-y-auto max-h-[calc(90vh-80px)]">
              <form onSubmit={onSubmit} className="p-2 space-y-6">
                {/* Basic Information */}
                <div className="p-4 rounded-md">
                  <h4 className="font-semibold text-gray-800 dark:text-white mb-4">
                    Basic Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    <div className="flex flex-col gap-2">
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Employee ID *
                      </label>
                      <select
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        value={form.employee_id}
                        onChange={(e) =>
                          setForm({ ...form, employee_id: e.target.value })
                        }
                      >
                        <option value="">Select Employee</option>
                        {allUsersWithEmployeeIds.map((user) => (
                          <option key={user.id} value={user.employee_id}>
                            {user.name} ({user.employee_id})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Gross Salary *
                      </label>
                      <input
                        type="number"
                        className="border border-gray-300 rounded-md px-3 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        value={form.gross_salary}
                        min={0}
                        onChange={(e) =>
                          setForm({ ...form, gross_salary: e.target.value })
                        }
                        required
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Basic Salary *
                      </label>
                      <input
                        type="number"
                        className="border border-gray-300 rounded-md px-3 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        value={form.basic_salary}
                        min={0}
                        onChange={(e) =>
                          setForm({ ...form, basic_salary: e.target.value })
                        }
                        required
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        CTC *
                      </label>
                      <input
                        type="number"
                        className="border border-gray-300 rounded-md px-3 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        value={form.ctc}
                        min={0}
                        onChange={(e) =>
                          setForm({ ...form, ctc: e.target.value })
                        }
                        required
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Effective From *
                      </label>
                      <input
                        type="date"
                        className="border border-gray-300 rounded-md px-3 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        value={form.effective_from}
                        onChange={(e) =>
                          setForm({ ...form, effective_from: e.target.value })
                        }
                        required
                      />
                    </div>
                  </div>

                  {editingEmployeeId && (
                    <div className="mt-4 flex flex-col sm:flex-row">
                      <button
                        type="button"
                        onClick={onGenerateTemplate}
                        className="w-full sm:w-auto md:text-md text-sm border-2 border-blue-500 text-blue-500 px-3 py-2 rounded-lg font-medium transition-colors shadow-sm flex justify-center"
                      >
                        Generate Template from Configurations
                      </button>
                    </div>
                  )}
                </div>

                {/* Earnings Components */}
                <div className="p-4 rounded-md">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
                    <h4 className="font-semibold text-gray-800 dark:text-white">
                      Earnings Components
                    </h4>
                    <button
                      type="button"
                      onClick={() => addComponentRow("earnings_components")}
                      className="w-full md:text-md text-sm sm:w-auto border-2 border-blue-500 text-blue-500 px-3 py-2 rounded-md font-medium transition-colors shadow-sm flex justify-center"
                    >
                      + Add Earning
                    </button>
                  </div>

                  <div className="space-y-4">
                    {(form.earnings_components || []).map((row, idx) => (
                      <div
                        key={`earn-${idx}`}
                        className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-600"
                      >
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                          <input
                            className="border border-gray-300 rounded-md px-3 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Component Name"
                            value={row.name}
                            onChange={(e) =>
                              updateComponentRow("earnings_components", idx, {
                                name: e.target.value,
                              })
                            }
                          />
                          <input
                            type="number"
                            className="border border-gray-300 rounded-md px-3 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Amount"
                            value={row.amount}
                            min={0}
                            onChange={(e) =>
                              updateComponentRow("earnings_components", idx, {
                                amount: e.target.value,
                              })
                            }
                          />
                          <input
                            type="number"
                            className="border border-gray-300 rounded-md px-3 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Config ID"
                            value={row.config_id}
                            min={0}
                            onChange={(e) =>
                              updateComponentRow("earnings_components", idx, {
                                config_id: e.target.value,
                              })
                            }
                          />
                          <div className="flex items-center justify-between">
                            <label className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                              <input
                                type="checkbox"
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                checked={!!row.is_taxable}
                                onChange={(e) =>
                                  updateComponentRow(
                                    "earnings_components",
                                    idx,
                                    { is_taxable: e.target.checked }
                                  )
                                }
                              />
                              Taxable
                            </label>
                            <button
                              type="button"
                              onClick={() =>
                                removeComponentRow("earnings_components", idx)
                              }
                              className="border-2 border-red-500 md:text-md text-sm text-red-500 px-3 py-1 rounded-lg font-medium transition-colors shadow-sm"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                    {(form.earnings_components || []).length === 0 && (
                      <p className="text-gray-500 dark:text-gray-400 text-center py-4">
                        No earnings components added yet
                      </p>
                    )}
                  </div>
                </div>

                {/* Deduction Components */}
                <div className="p-4 rounded-md">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
                    <h4 className="font-semibold text-gray-800 dark:text-white">
                      Deduction Components
                    </h4>
                    <button
                      type="button"
                      onClick={() => addComponentRow("deduction_components")}
                      className="w-full md:text-md text-sm sm:w-auto border-2 border-blue-500 text-blue-500 px-3 py-2 rounded-lg font-medium transition-colors shadow-sm flex justify-center"
                    >
                      + Add Deduction
                    </button>
                  </div>
                  <div className="space-y-4">
                    {(form.deduction_components || []).map((row, idx) => (
                      <div
                        key={`ded-${idx}`}
                        className="bg-white dark:bg-gray-800 p-4 rounded-md border border-gray-200 dark:border-gray-600"
                      >
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                          <input
                            className="border border-gray-300 rounded-md px-3 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Component Name"
                            value={row.name}
                            onChange={(e) =>
                              updateComponentRow("deduction_components", idx, {
                                name: e.target.value,
                              })
                            }
                          />
                          <input
                            type="number"
                            className="border border-gray-300 rounded-md px-3 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Amount"
                            value={row.amount}
                            min={0}
                            onChange={(e) =>
                              updateComponentRow("deduction_components", idx, {
                                amount: e.target.value,
                              })
                            }
                          />
                          <input
                            type="number"
                            className="border border-gray-300 rounded-md px-3 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Config ID"
                            value={row.config_id}
                            min={0}
                            onChange={(e) =>
                              updateComponentRow("deduction_components", idx, {
                                config_id: e.target.value,
                              })
                            }
                          />
                          <div className="flex items-center justify-between">
                            <label className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                              <input
                                type="checkbox"
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                checked={!!row.is_taxable}
                                onChange={(e) =>
                                  updateComponentRow(
                                    "deduction_components",
                                    idx,
                                    { is_taxable: e.target.checked }
                                  )
                                }
                              />
                              Taxable
                            </label>
                            <button
                              type="button"
                              onClick={() =>
                                removeComponentRow("deduction_components", idx)
                              }
                              className="border-2 border-red-500 md:text-md text-sm text-red-500 px-3 py-1 rounded-lg font-medium transition-colors shadow-sm"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                    {(form.deduction_components || []).length === 0 && (
                      <p className="text-gray-500 dark:text-gray-400 text-center py-4">
                        No deduction components added yet
                      </p>
                    )}
                  </div>
                </div>

                {/* Variable Components */}
                <div className="p-4 rounded-md">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
                    <h4 className="font-semibold text-gray-800 dark:text-white">
                      Variable Components
                    </h4>
                    <button
                      type="button"
                      onClick={() => addComponentRow("variable_components")}
                      className="w-full md:text-md text-sm sm:w-auto border-2 border-blue-500 text-blue-500 px-3 py-2 rounded-lg font-medium transition-colors shadow-sm flex justify-center"
                    >
                      + Add Variable
                    </button>
                  </div>
                  <div className="space-y-4">
                    {(form.variable_components || []).map((row, idx) => (
                      <div
                        key={`var-${idx}`}
                        className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-600"
                      >
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                          <input
                            className="border border-gray-300 rounded-md px-3 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Component Name"
                            value={row.name}
                            onChange={(e) =>
                              updateComponentRow("variable_components", idx, {
                                name: e.target.value,
                              })
                            }
                          />
                          <input
                            type="number"
                            className="border border-gray-300 rounded-md px-3 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Amount"
                            value={row.amount}
                            min={0}
                            onChange={(e) =>
                              updateComponentRow("variable_components", idx, {
                                amount: e.target.value,
                              })
                            }
                          />
                          <input
                            type="number"
                            className="border border-gray-300 rounded-md px-3 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Config ID"
                            value={row.config_id}
                            min={0}
                            onChange={(e) =>
                              updateComponentRow("variable_components", idx, {
                                config_id: e.target.value,
                              })
                            }
                          />
                          <div className="flex items-center justify-between">
                            <label className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                              <input
                                type="checkbox"
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                checked={!!row.is_taxable}
                                onChange={(e) =>
                                  updateComponentRow(
                                    "variable_components",
                                    idx,
                                    { is_taxable: e.target.checked }
                                  )
                                }
                              />
                              Taxable
                            </label>
                            <button
                              type="button"
                              onClick={() =>
                                removeComponentRow("variable_components", idx)
                              }
                              className="border-2 border-red-500 md:text-md text-sm text-red-500 px-3 py-1 rounded-lg font-medium transition-colors shadow-sm"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                    {(form.variable_components || []).length === 0 && (
                      <p className="text-gray-500 dark:text-gray-400 text-center py-4">
                        No variable components added yet
                      </p>
                    )}
                  </div>
                </div>

                {/* Form Actions */}
                <div className="flex justify-end gap-2 px-4 pb-3 border-gray-200 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-8 py-2 hover:bg-gray-200 dark:hover:bg-gray-900 dark:bg-black dark:text-white dark:focus:outline-none border border-gray-300 rounded-md"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
                  >
                    {editingEmployeeId
                      ? "Update Structure"
                      : "Create Structure"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* History Modal */}
      {showHistory && (
        <div className="fixed inset-0 bg-black/60 flex justify-center items-end md:items-center z-[1001] backdrop-blur-md transition-all duration-300">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowHistory(false)}
          ></div>
          <div className="relative w-full max-w-4xl bg-white dark:bg-gray-900 rounded-md shadow-xl max-h-[80vh] overflow-hidden">
            <div className="relative">
              <div className="absolute inset-0 dark:from-gray-800 dark:to-gray-850"></div>
              <div className="relative flex items-center justify-between p-4">
                <div className="flex items-center space-x-3">
                  {/* <div className="w-1 h-6 bg-gradient-to-b from-blue-500 to-purple-600 rounded-full"></div> */}
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white tracking-tight">
                    Salary History - {historyEmployee}
                  </h2>
                </div>

                {/* Enhanced close button */}
                <button
                  type="button"
                  onClick={() => setShowHistory(false)}
                  className="group relative p-2 rounded-xl transition-all duration-200 hover:scale-105 active:scale-95"
                  aria-label="Close modal"
                >
                  <X
                    size={20}
                    className="text-gray-600 transition-colors duration-200"
                  />
                  <div className="absolute inset-0 rounded-xl transition-all duration-200"></div>
                </button>
              </div>
            </div>

            <div className="p-2 overflow-y-auto max-h-[calc(80vh-80px)]">
              {historyLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3"></div>
                  Loading history...
                </div>
              ) : historyItems.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  No salary history found for this employee
                </div>
              ) : (
                <div className="overflow-x-auto bg-white dark:bg-gray-900 shadow-md rounded-lg font-poppins mx-2">
                  <table className="min-w-full table-auto md:table-fixed text-sm">
                    <thead className="bg-gray-100 dark:bg-gray-800 dark:text-white">
                      <tr>
                        <th className="whitespace-nowrap text-left px-4 py-3 font-semibold text-gray-700 dark:text-gray-300">
                          Effective From
                        </th>
                        <th className="whitespace-nowrap text-left px-4 py-3 font-semibold text-gray-700 dark:text-gray-300">
                          Basic Salary
                        </th>
                        <th className="whitespace-nowrap text-left px-4 py-3 font-semibold text-gray-700 dark:text-gray-300">
                          Gross Salary
                        </th>
                        <th className="whitespace-nowrap text-left px-4 py-3 font-semibold text-gray-700 dark:text-gray-300">
                          CTC
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {historyItems.map((h, idx) => (
                        <tr
                          key={idx}
                          className="hover:bg-gray-50 dark:hover:bg-gray-800/50"
                        >
                          {/* <td className="px-4 py-3 text-gray-900 dark:text-white">{h.effective_from}</td> */}
                          <td className="px-6 py-4 text-gray-700 dark:text-gray-300">
                            {h.effective_from.slice(8, 10)}-
                            {h.effective_from.slice(5, 7)}-
                            {h.effective_from.slice(0, 4)}
                          </td>
                          <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                            ₹{h.basic_salary?.toLocaleString() || "N/A"}
                          </td>
                          <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                            ₹{h.gross_salary?.toLocaleString() || "N/A"}
                          </td>
                          <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                            ₹{h.ctc?.toLocaleString() || "N/A"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="px-6 py-4 flex justify-end">
              <button
                onClick={() => setShowHistory(false)}
                className="px-12 py-2 text-black hover:bg-gray-200 dark:hover:bg-gray-900 dark:bg-black dark:text-white dark:focus:outline-none border border-gray-300 rounded-md"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Update Modal */}
      {showBulk && (
        <div className="fixed inset-0 bg-black/60 flex justify-center items-end md:items-center z-[1001] backdrop-blur-md transition-all duration-300">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowBulk(false)}
          ></div>
          <div className="relative w-full max-w-5xl bg-white dark:bg-gray-900 rounded-md shadow-xl max-h-[90vh] overflow-hidden">
            <div className="relative">
              <div className="absolute inset-0 dark:from-gray-800 dark:to-gray-850"></div>
              <div className="relative flex items-center justify-between py-3 px-6 ">
                <div className="flex items-center space-x-3">
                  {/* <div className="w-1 h-6 bg-gradient-to-b from-blue-500 to-purple-600 rounded-full"></div> */}
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white tracking-tight">
                    Bulk Update Salary Structures
                  </h2>
                </div>

                {/* Enhanced close button */}
                <button
                  type="button"
                  onClick={() => setShowBulk(false)}
                  className="group relative p-2 rounded-xl transition-all duration-200 hover:scale-105 active:scale-95"
                  aria-label="Close modal"
                >
                  <X
                    size={20}
                    className="text-gray-600 transition-colors duration-200"
                  />
                  <div className="absolute inset-0 rounded-xl transition-all duration-200"></div>
                </button>
              </div>
            </div>

            <div className="overflow-y-auto max-h-[calc(90vh-150px)]">
              <div className="p-6 space-y-6">
                {/* Effective Date */}
                <div className="bg-gray-50 dark:bg-gray-900/20 rounded-lg">
                  <div className="flex flex-col gap-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Effective From *
                    </label>
                    <input
                      type="date"
                      className="border border-gray-300 rounded-lg px-3 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full max-w-xs"
                      value={bulkEffectiveFrom}
                      onChange={(e) => setBulkEffectiveFrom(e.target.value)}
                      required
                    />
                  </div>
                </div>

                {/* Employee Updates */}
                <div className="bg-gray-50 dark:bg-gray-900/20 rounded-lg">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-semibold text-gray-800 dark:text-white">
                      Employee Salary Updates
                    </h4>
                    <button
                      type="button"
                      onClick={addBulkUpdateRow}
                      className="border-2 border-blue-500 text-blue-500 px-3 py-1 rounded-lg font-medium transition-colors shadow-sm"
                    >
                      + Add Employee
                    </button>
                  </div>

                  <div className="space-y-4">
                    {bulkUpdates.map((update, index) => (
                      <div
                        key={index}
                        className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-purple-200 dark:border-purple-800"
                      >
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                          {/* Employee ID */}
                          <div className="md:col-span-3">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Employee ID *
                            </label>
                            <select
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                              value={update.employee_id}
                              onChange={(e) =>
                                updateBulkUpdateRow(
                                  index,
                                  "employee_id",
                                  e.target.value
                                )
                              }
                            >
                              <option value="">Select Employee</option>
                              {allUsersWithEmployeeIds.map((user) => (
                                <option key={user.id} value={user.employee_id}>
                                  {user.name} ({user.employee_id})
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Update Type */}
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Update Type
                            </label>
                            <select
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                              value={update.update_type}
                              onChange={(e) =>
                                updateBulkUpdateRow(
                                  index,
                                  "update_type",
                                  e.target.value
                                )
                              }
                            >
                              <option value="percentage">Percentage</option>
                              <option value="fixed">Fixed Amount</option>
                            </select>
                          </div>

                          {/* Percentage Increase */}
                          {update.update_type === "percentage" && (
                            <div className="md:col-span-2">
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Percentage (%) *
                              </label>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                placeholder="e.g., 10.5"
                                value={update.percentage_increase}
                                onChange={(e) =>
                                  updateBulkUpdateRow(
                                    index,
                                    "percentage_increase",
                                    e.target.value
                                  )
                                }
                              />
                            </div>
                          )}

                          {/* Fixed Increase */}
                          {update.update_type === "fixed" && (
                            <div className="md:col-span-2">
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Fixed Amount (₹) *
                              </label>
                              <input
                                type="number"
                                min="0"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                placeholder="e.g., 5000"
                                value={update.fixed_increase}
                                onChange={(e) =>
                                  updateBulkUpdateRow(
                                    index,
                                    "fixed_increase",
                                    e.target.value
                                  )
                                }
                              />
                            </div>
                          )}

                          {/* Spacer for alignment */}
                          <div
                            className={`${
                              update.update_type === "percentage"
                                ? "md:col-span-3"
                                : "md:col-span-3"
                            }`}
                          ></div>

                          {/* Remove Button */}
                          <div className="md:col-span-2 flex justify-end">
                            {bulkUpdates.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeBulkUpdateRow(index)}
                                className="border-2 border-red-500 text-red-500 px-3 py-1 rounded-lg font-medium transition-colors shadow-sm"
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Preview calculation */}
                        {update.employee_id &&
                          (update.percentage_increase ||
                            update.fixed_increase) && (
                            <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                              <p className="text-sm text-gray-600 dark:text-gray-300">
                                <span className="font-medium">Preview:</span>{" "}
                                Employee {update.employee_id} will receive a{" "}
                                {update.update_type === "percentage"
                                  ? `${update.percentage_increase}% increase`
                                  : `₹${Number(
                                      update.fixed_increase || 0
                                    ).toLocaleString()} fixed increase`}
                              </p>
                            </div>
                          )}
                      </div>
                    ))}

                    {bulkUpdates.length === 0 && (
                      <div className="text-center py-8">
                        <p className="text-gray-500 dark:text-gray-400 mb-4">
                          No employee updates added yet
                        </p>
                        <button
                          type="button"
                          onClick={addBulkUpdateRow}
                          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
                        >
                          + Add First Employee
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Summary */}
                {bulkUpdates.some((update) => update.employee_id) && (
                  <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-800 dark:text-white mb-2">
                      Summary
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      {
                        bulkUpdates.filter((update) => update.employee_id)
                          .length
                      }{" "}
                      employee(s) will receive salary updates
                      {bulkEffectiveFrom &&
                        ` effective from ${new Date(
                          bulkEffectiveFrom
                        ).toLocaleDateString()}`}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="px-6 py-4 border-gray-200 dark:border-gray-700 flex justify-end gap-2">
              <button
                onClick={() => setShowBulk(false)}
                className="px-12 py-2 font-medium text-black hover:bg-gray-200 dark:hover:bg-gray-900 dark:bg-black dark:text-white dark:focus:outline-none border border-gray-300 rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={onBulkUpdate}
                disabled={
                  !bulkEffectiveFrom ||
                  !bulkUpdates.some((update) => update.employee_id)
                }
                className="px-6 py-2 font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
              >
                Apply Bulk Update (
                {bulkUpdates.filter((update) => update.employee_id).length}{" "}
                employees)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mx-2 p-4 rounded-lg bg-red-50 border border-red-200 dark:bg-red-900/20 dark:border-red-800">
          <div className="flex items-center">
            <svg
              className="w-5 h-5 text-red-400 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p className="text-red-700 dark:text-red-300 font-medium">
              {error}
            </p>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/60 flex justify-center items-center z-[1001]">
          <div className="bg-white dark:bg-gray-800 rounded-md shadow-xl max-w-md w-full ">
            {/* <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Import CSV
              </h3>
              <button
                onClick={() => setShowImportModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div> */}

            <div className="relative">
              <div className="absolute inset-0 dark:from-gray-800 dark:to-gray-850"></div>
              <div className="relative flex items-center justify-between p-3">
                <div className="flex items-center space-x-3">
                  {/* <div className="w-1 h-6 bg-gradient-to-b from-blue-500 to-purple-600 rounded-full"></div> */}
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white tracking-tight">
                    Import CSV
                  </h2>
                </div>

                {/* Enhanced close button */}
                <button
                  type="button"
                  onClick={() => setShowImportModal(false)}
                  className="group relative p-2 rounded-xl transition-all duration-200 hover:scale-105 active:scale-95"
                  aria-label="Close modal"
                >
                  <X
                    size={20}
                    className="text-gray-600 transition-colors duration-200"
                  />
                  <div className="absolute inset-0 rounded-xl transition-all duration-200"></div>
                </button>
              </div>
            </div>

            <div className="flex flex-col gap-4 p-4">
              <input
                type="file"
                accept=".csv"
                onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                className="border border-gray-300 rounded-md p-2"
              />
              {importFile && (
                <button
                  onClick={onImportCsv}
                  className="bg-blue-600 text-white px-4 py-2 rounded-md font-medium hover:bg-blue-700 transition-colors"
                >
                  Upload CSV
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
